import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../lib/api";
import { CURRENCIES, fmt, fmtIDR, fmtNum, typeColor } from "../lib/format";
import { Search, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Modal } from "./OfficerHome";

const RANGES = [
  { k: "today", l: "Hari Ini" }, { k: "yesterday", l: "Kemarin" },
  { k: "week", l: "Minggu Ini" }, { k: "month", l: "Bulan Ini" },
];
const TYPES = ["", "BUY", "SELL", "EXCHANGE", "PULSA", "TELE", "REVERSAL"];

export default function Transactions() {
  const { user } = useAuth();
  const [range, setRange] = useState("today");
  const [type, setType] = useState("");
  const [currency, setCurrency] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [voidTxn, setVoidTxn] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ range });
      if (type) p.set("type", type);
      if (currency) p.set("currency", currency);
      if (search) p.set("search", search);
      const r = await api.get(`/transactions?${p.toString()}`);
      setRows(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [range, type, currency, search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-4">
      <h1 className="font-head text-2xl font-bold">Transaksi</h1>

      <div className="nnx-card p-4 space-y-3">
        <div className="flex gap-2 overflow-x-auto pb-1">
          {RANGES.map((r) => (
            <button key={r.k} data-testid={`range-${r.k}`} onClick={() => setRange(r.k)}
              className={`chip whitespace-nowrap ${range === r.k ? "bg-[#EAB308] text-[#0B0E14] border-[#EAB308]" : ""}`}>{r.l}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <select data-testid="filter-type" value={type} onChange={(e) => setType(e.target.value)} className="nnx-input w-auto text-sm">
            {TYPES.map((t) => <option key={t} value={t}>{t || "Semua Tipe"}</option>)}
          </select>
          <select data-testid="filter-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className="nnx-input w-auto text-sm">
            <option value="">Semua Currency</option>
            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
          </select>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="h-4 w-4 absolute left-3 top-3 text-[#64748B]" />
            <input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari ID / customer / catatan" className="nnx-input pl-9 text-sm" />
          </div>
        </div>
      </div>

      <div className="hidden lg:block nnx-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#64748B] border-b border-[#222B3D]">
              {["ID", "Waktu", "Officer", "Tipe", "Pair", "Beri", "Terima", "Rate", "Profit", "Status", ""].map((h) => <th key={h} className="px-3 py-2.5 whitespace-nowrap text-xs uppercase tracking-wider">{h}</th>)}
            </tr>
          </thead>
          <tbody data-testid="txn-table-body">
            {loading ? (
              <tr><td colSpan={11} className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin inline text-[#EAB308]" /></td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={11} className="text-center py-10 text-[#64748B]">Belum ada transaksi</td></tr>
            ) : rows.map((t) => {
              const tc = typeColor(t.type);
              return (
                <tr key={t.txn_code} className="border-b border-[#222B3D]/60 hover:bg-[#1C2436]/50" data-testid={`txn-row-${t.txn_code}`}>
                  <td className="px-3 py-2.5 mono text-xs whitespace-nowrap">{t.txn_code}</td>
                  <td className="px-3 py-2.5 text-xs text-[#94A3B8] whitespace-nowrap">{new Date(t.created_at).toLocaleString("id-ID", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap">{t.officer_name}</td>
                  <td className="px-3 py-2.5"><span className={`text-xs font-bold ${tc.text}`}>{t.type}</span></td>
                  <td className="px-3 py-2.5 mono text-xs">{t.pair}</td>
                  <td className="px-3 py-2.5 mono text-xs whitespace-nowrap">{fmt(t.gives_amount, t.gives_asset)}</td>
                  <td className="px-3 py-2.5 mono text-xs whitespace-nowrap">{fmt(t.receives_amount, t.receives_asset)}</td>
                  <td className="px-3 py-2.5 mono text-xs">{fmtNum(t.rate)}</td>
                  <td className={`px-3 py-2.5 mono text-xs whitespace-nowrap ${Number(t.net_profit) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtIDR(t.net_profit)}</td>
                  <td className="px-3 py-2.5"><StatusPill s={t.status} /></td>
                  <td className="px-3 py-2.5">
                    {user?.role === "owner" && t.status === "completed" && (
                      <button data-testid={`void-${t.txn_code}`} onClick={() => setVoidTxn(t)} className="text-rose-400 hover:text-rose-300"><Ban className="h-4 w-4" /></button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-2" data-testid="txn-card-list">
        {loading ? (
          <div className="nnx-card p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-[#EAB308]" /></div>
        ) : rows.length === 0 ? (
          <div className="nnx-card p-8 text-center text-[#64748B]">Belum ada transaksi</div>
        ) : rows.map((t) => {
          const tc = typeColor(t.type);
          return (
            <div key={t.txn_code} className="nnx-card p-3" data-testid={`txn-card-${t.txn_code}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0">
                  <div className="mono text-[11px] text-[#64748B] truncate">{t.txn_code}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`text-xs font-bold ${tc.text}`}>{t.type}</span>
                    <span className="mono text-xs text-[#94A3B8]">{t.pair}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`mono text-sm font-bold ${Number(t.net_profit) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtIDR(t.net_profit)}</div>
                  <StatusPill s={t.status} />
                </div>
              </div>
              <div className="flex justify-between items-center mt-2 gap-2">
                <span className="mono text-xs text-[#CBD5E1] truncate">{fmt(t.gives_amount, t.gives_asset)} → {fmt(t.receives_amount, t.receives_asset)}</span>
                {user?.role === "owner" && t.status === "completed" && (
                  <button data-testid={`void-m-${t.txn_code}`} onClick={() => setVoidTxn(t)} className="text-rose-400 shrink-0"><Ban className="h-4 w-4" /></button>
                )}
              </div>
              <div className="text-[10px] text-[#64748B] mt-1">{t.officer_name} · {new Date(t.created_at).toLocaleString("id-ID", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "short" })}</div>
            </div>
          );
        })}
      </div>

      {voidTxn && <VoidModal txn={voidTxn} onClose={() => setVoidTxn(null)} onDone={() => { setVoidTxn(null); load(); }} />}
    </div>
  );
}

function StatusPill({ s }) {
  const m = {
    completed: "text-emerald-400 bg-emerald-500/15", pending_approval: "text-amber-400 bg-amber-500/15",
    voided: "text-rose-400 bg-rose-500/15", rejected: "text-rose-400 bg-rose-500/15",
    reversal: "text-sky-400 bg-sky-500/15",
  };
  return <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${m[s] || "text-[#94A3B8] bg-[#222B3D]"}`}>{s}</span>;
}

function VoidModal({ txn, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!reason.trim()) { toast.error("Alasan wajib diisi"); return; }
    setLoading(true);
    try {
      await api.post(`/transactions/${txn.txn_code}/void`, { reason });
      toast.success("Transaksi di-void (reversal dibuat)");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  return (
    <Modal title={`Void ${txn.txn_code}`} onClose={onClose} testid="void-modal">
      <p className="text-sm text-[#94A3B8] mb-3">Transaksi asli tetap tersimpan; sistem membuat reversal inventory & profit.</p>
      <textarea data-testid="void-reason" className="nnx-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan void" />
      <button className="btn-gold w-full mt-4 bg-rose-500 hover:bg-rose-600 text-white" data-testid="confirm-void" onClick={submit} disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "VOID / REVERSE"}</button>
    </Modal>
  );
}
